import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Box, TableContainer, TableHead, TableRow, TableCell, TableBody, Table, Paper, useTheme, Pagination, IconButton, TextField } from '@mui/material';
import { blue, grey } from '@mui/material/colors';
import { Add } from '@mui/icons-material';

import { useAuth } from '../../Auth/AuthContext';

import RowTableTarifa from '../SubComponents/Tarifas/RowTableTarifa';
import DialogCreateTarifa from '../SubComponents/Tarifas/DialogCreateTarifa';
import EmptyState from '../common/EmptyState';
import { getTopSearchMatchIds } from '../../utils/searchHighlights';

const API_URL = process.env.REACT_APP_API_URL;

function bubbleSort(arr) {
  let n = arr.length;
  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j].id < arr[j + 1].id) {
        [arr[j], arr[j + 1]] = [arr[j + 1], arr[j]]; // Ordenar por ID de forma descendente
      }
    }
  }
  return arr;
}

function Tarifas() {
  const { user } = useAuth();
  const areUserAdmin = user.rol === 'Administrador';

  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [tarifas, setTarifas] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [refresh, setRefresh] = useState(true)
  const rowsPerPage = 8;

  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const handleCloseDialogCreate = () => { setOpenDialogCreate(false) }
  const handleRefreshData = () => {setRefresh(!refresh)}

  useEffect(() => {
    setLoading(true);
    setLoadError(false);
    axios.get(`${API_URL}/tarifas/`)
      .then(response => {
        setTarifas(response.data);
      })
      .catch(error => {
        console.error('Error fetching tarifas:', error);
        setLoadError(true);
      })
      .finally(() => setLoading(false));
  }, [refresh]);

  useEffect(() => {
    setTarifas(prevTarifas => bubbleSort([...prevTarifas]));
  }, [tarifas]);

  const handleDeleteTarifa = (id) => {
    axios.delete(`${API_URL}/tarifas/${id}/`)
      .then(() => {
        setTarifas(prevTarifas => prevTarifas.filter(tarifa => tarifa.id !== id));
      })
      .catch(error => {
        console.error('Error deleting tarifas:', error);
      });
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredTarifas = tarifas.filter(tarifa =>
    tarifa.id.toString().includes(searchTerm) ||
    tarifa.tarifa_inicial.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tarifa.horas_iniciales.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tarifa.precio.toLowerCase().includes(searchTerm.toLowerCase())  ||
    tarifa.tarifa_adicional.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tarifa.descuento.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tarifa.fecha_creacion.toLowerCase().includes(searchTerm.toLowerCase()) ||
    tarifa.fecha_modificacion.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentTarifas = bubbleSort(filteredTarifas.slice(startIndex, startIndex + rowsPerPage));
  const searchHighlightIds = useMemo(() => new Set(getTopSearchMatchIds(
    currentTarifas,
    searchTerm,
    ['id', 'nombre', 'tarifa_inicial', 'horas_iniciales', 'tarifa_adicional', 'descuento']
  )), [currentTarifas, searchTerm]);
  const handleUpdateData = (updatedTarifa) => { setTarifas((prevData) => prevData.map((tarifa) => tarifa.id === updatedTarifa.id ? updatedTarifa : tarifa));};

  return (
    <Box sx={{ paddingTop: 1, position: 'relative', height: '100%' }}>
      <TextField 
        label="Buscar Tarifa Por Cualquier Dato"
        variant="outlined"
        sx={{ paddingBottom: 2 }}
        fullWidth
        margin="normal"
        value={searchTerm}
        onChange={handleSearchChange}
      />
        
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650, borderCollapse: 'separate', borderSpacing: 0 }}>
          <TableHead sx={{
            backgroundColor: isDarkMode ? grey[800] : blue[700],
            color: isDarkMode ? 'black' : 'white',
          }}>
            <TableRow>
            <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>ID</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>NOMBRE TARIFA</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>TARIFA INICIAL PAGO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>HORAS INICIALES DE TARIFA</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>TARIFA ADICIONAL</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>DESCUENTO</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>FECHA CREACIÓN</TableCell>
              <TableCell align="left" sx={{ borderLeft: '2px solid #ddd', borderRight: areUserAdmin ? null : '2px solid #ddd', borderBottom: '2px solid #ddd', fontWeight: 'bold', color: 'white' }}>FECHA ULTIMA MODIFICACIÓN</TableCell>
              {areUserAdmin && <TableCell align="center"sx={{borderLeft: '2px solid #ddd',borderBottom: '2px solid #ddd',borderRight: '2px solid #ddd',fontWeight: 'bold',color: 'white',verticalAlign: 'middle'}}>
                  ACCIONES
                  <IconButton onClick={() => {setOpenDialogCreate(true)}}>
                     <Add sx={{ color: 'white' }}/>
                  </IconButton>
              </TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && currentTarifas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={areUserAdmin ? 9 : 8}>
                  <EmptyState message={loadError ? 'No fue posible cargar las tarifas.' : searchTerm ? 'No se encontraron resultados.' : 'No hay tarifas para mostrar.'} />
                </TableCell>
              </TableRow>
            ) : currentTarifas.map((item) => (
              <RowTableTarifa key={item.id} data={item} handleDelete={handleDeleteTarifa} handleUpdateData={handleUpdateData} areUserAdmin={areUserAdmin} searchHighlighted={searchHighlightIds.has(item.id)}/>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
        <Pagination 
          count={Math.ceil(tarifas.length / rowsPerPage)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <DialogCreateTarifa openDialog={openDialogCreate} handleCloseDialog={handleCloseDialogCreate} setOpenDialog={setOpenDialogCreate} handleRefreshData={handleRefreshData}/>
    </Box>
  );
}

export default Tarifas;
